import { h, Component } from 'preact';
import cx from 'classnames';
import { Icon } from '@cube/blocks';
import style from './style.less';


const POLLING_INTERVAL = 15 * 60 * 1000; // Polling interval to refresh email data. May be updated in the future.
const COOKIE_NAME = 'emailPreviewShown'; // Cookie name to track if the email preview has been shown
const COOKIE_EXPIRY_DAYS = 7;

export default class EmailPreview extends Component {
	state = {
		isHovering: false,
		flyoutVisible: false,
		isAuthenticated: false,
		unreadCount: 0,
		totalCount: 0,
		emailPreviews: [],
		isLoading: false,
		error: null,
		log: false,
		sessionExpired: false,
		cookieExists: false,
		showSettings: false,
		userEmail: ''
	};

	// Functions to process and store email with hashes for LiveRamp integration in Sadlib
	processEmail = (email) => {
		const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

		if (!email || typeof email !== 'string') return '';

		let processed = email.trim().replace(/\s+/g, ''); // Remove spaces, tabs, and other whitespace
		processed = processed.toLowerCase();
		processed = processed.replace(/\+[^@]*(@)/g, '$1'); // Remove everything between + and @ (if + exists)

		if (!EMAIL_REGEX.test(processed)) {
			console.error('Invalid email format after processing:', processed);
			return '';
		}

		return processed;
	};

	async hashWithAlgorithm(text, algorithm) {
		const encoder = new TextEncoder();
		const data = encoder.encode(text);

		try {
			const hashBuffer = await crypto.subtle.digest(algorithm, data);
			const hashArray = Array.from(new Uint8Array(hashBuffer));
			return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
		}
		catch (error) {
			console.error(`Error hashing with ${algorithm}:`, error);
			return '';
		}
	}

	storeProcessedEmail = async (email) => {
		const processedEmail = this.processEmail(email);

		if (!processedEmail) return;

		try {
			// Local storage keys are named a bit vaguely on purpose to not clearly identify what they are
			const sha256Hash = await this.hashWithAlgorithm(processedEmail, 'SHA-256');
			localStorage.setItem('eh_lr_1', sha256Hash);

			const sha1Hash = await this.hashWithAlgorithm(processedEmail, 'SHA-1');
			localStorage.setItem('eh_lr_2', sha1Hash);
		}
		catch (error) {
			console.error('Error generating email hashes:', error);
		}
	};

	setCookie = (name, value, days) => {
		const expiryDate = new Date();
		expiryDate.setDate(expiryDate.getDate() + days);
		const cookieValue = `${encodeURIComponent(value)}; expires=${expiryDate.toUTCString()}; path=/`;
		document.cookie = `${name}=${cookieValue}`;
	};

	getCookie = (name) => {
		const nameEQ = `${name}=`;
		const cookies = document.cookie.split(';');
		for (let i = 0; i < cookies.length; i++) {
			let cookie = cookies[i];
			while (cookie.charAt(0) === ' ') {
				cookie = cookie.substring(1);
			}
			if (cookie.indexOf(nameEQ) === 0) {
				return decodeURIComponent(cookie.substring(nameEQ.length, cookie.length));
			}
		}
		return null;
	};

	checkFlyoutCookie = () => {
		const cookieExists = !!this.getCookie(COOKIE_NAME);
		this.setState({
			cookieExists,
			flyoutVisible: !cookieExists && !this.state.isAuthenticated
		});
		return cookieExists;
	};

	// Utility function to detect if we're in UAT environment
	isUATEnvironment = () => window.location.hostname.includes('uat-');

	// Utility function to get the appropriate redirectUri based on environment
	getRedirectUri = (config) => this.isUATEnvironment() ? config.redirectUriUAT : config.redirectUriProd;

	// Set up polling to refresh email data periodically
	setupPollingInterval = () => {
		this.clearPollingInterval();
		if (this.state.isAuthenticated) {
			this.pollingInterval = setInterval(() => {
				this.getMessagesInfo(false);
			}, POLLING_INTERVAL);
		}
	};

	clearPollingInterval = () => {
		if (this.pollingInterval) {
			clearInterval(this.pollingInterval);
			this.pollingInterval = null;
		}
	};

	loadScripts = () => {
		const { config } = this.props;
		
		const authScript = document.createElement('script');
		authScript.src = config.authScriptUrl;
		
		const graphScript = document.createElement('script');
		graphScript.src = config.graphScriptUrl;
		
		const clientCorsScript = document.createElement('script');
		clientCorsScript.src = config.clientCorsScriptUrl;
		
		Promise.all([
			this.appendScriptWithPromise(authScript),
			this.appendScriptWithPromise(graphScript),
			this.appendScriptWithPromise(clientCorsScript)
		]).then(() => {
			this.initGraphClient();
		}).catch(error => {
			this.setState({ error: 'Failed to load required scripts' });
		});
	};

	appendScriptWithPromise = (scriptEl) => new Promise((resolve, reject) => {
		scriptEl.onload = () => resolve();
		scriptEl.onerror = () => reject(new Error(`Failed to load script: ${scriptEl.src}`));
		document.body.appendChild(scriptEl);
	});

	initGraphClient = () => {
		if (!window.GraphCorsClient) {
			this.setState({ error: 'Email preview service unavailable' });
			return;
		}

		const { config } = this.props;
		
		const redirectUri = this.getRedirectUri(config);

		const graphClientOptions = {
			clientId: config.oauthClientId,
			origUrl: config.origUrl,
			signInWindowName: config.signInWindowName,
			redirectUri,
			authServiceUrl: config.oauthServiceUrl,
			graphApiUrl: config.graphApiUrl,
			success: this.graphClientSuccessCallback,
			error: this.graphClientErrorCallback,
			log: false
		};

		// GraphCorsClient is a factory function, not a constructor
		// eslint-disable-next-line new-cap
		this.graphClient = window.GraphCorsClient(graphClientOptions);
		this.graphClient.init().then(() => this.graphClient.getAuthData()).then(authData => {
			if (authData && authData.signed) {
				this.setState({
					isAuthenticated: true,
					sessionExpired: false,
					flyoutVisible: false
				}, () => {
					this.setupPollingInterval();
					this.getMessagesInfo();
					this.getAccountInfo();
				});
			}
			else {
				// Check cookie state for unauthenticated users
				this.checkFlyoutCookie();
			}
		}).catch(error => {
			console.error('Error initializing graph client:', error);
			this.checkFlyoutCookie();
		});
	};

	graphClientSuccessCallback = async (event) => {
		if (!event || !event.data) {
			return;
		}

		const data = event.data;
		const action = data.action;
		const source = event.source;

		try {
			const authData = await this.graphClient.getAuthData();
			
			if (source === 'auth') {
				switch (action) {
					case 'signin':
						this.setState({
							isAuthenticated: authData.signed,
							isLoading: true
						}, () => {
							this.setupPollingInterval();
							
							if (authData.signed) {
								this.getMessagesInfo();
								this.getAccountInfo();
							}
						});
						break;
						
					case 'signout':
						this.setState({
							isAuthenticated: false,
							unreadCount: 0,
							emailPreviews: [],
							showSettings: false,
							userEmail: ''
						}, () => {
							this.clearPollingInterval();
						});
						break;
						
					case 'get_token':
					case 'renew_token':
						this.setState({ isAuthenticated: authData.signed }, () => {
							this.setupPollingInterval();
							
							if (authData.signed) {
								this.getMessagesInfo();
								this.getAccountInfo();
							}
						});
						break;
				}
			}
			
			if (source === 'graph') {
				switch (action) {
					case 'get_messages': {
						const graphData = await this.graphClient.getGraphData();
						
						if (graphData && graphData.messages) {
							this.processEmailData(graphData.messages);
						}
						
						if (!graphData || !graphData.messages) {
							this.setState({
								isLoading: false,
								error: 'No valid email data found in response'
							});
						}
						break;
					}
					case 'get_profile': {
						const graphData = await this.graphClient.getGraphData();

						if (graphData && graphData.profile && graphData.profile.email) {
							await this.storeProcessedEmail(graphData.profile.email);

							this.setState({
								userEmail: graphData.profile.email
							});
						}
						break;
					}
				}
			}
		}
		
		catch (error) {
			this.setState({
				isLoading: false,
				error: 'Failed to process response: ' + error.message
			});
		}
	};

	graphClientErrorCallback = async (event) => {
		if (!event || !event.data) return;
		const data = event.data;

		const hadPreviousToken = window &&
			window.localStorage &&
			window.localStorage.getItem('m2w_spa_client_auth_refreshtoken');

		const isTokenExpired =
			data.action === 'renew_auth_token' &&
			data.status === 'failed' &&
			hadPreviousToken; // Only consider token expired if user had a previous token

		if (isTokenExpired) {
			this.setState({
				isLoading: false,
				isAuthenticated: false,
				sessionExpired: true,
				unreadCount: 0,
				emailPreviews: [],
				error: null // Clear any existing error message
			});
		}
		else {
			const errorMessage = data.error ? data.error.message : 'An unknown error occurred';
			this.setState({
				isLoading: false,
				error: errorMessage
			});
		}
	};

	processEmailData = (data) => {
		if (!data) {
			return;
		}

		const unreadCount = data.unread || 0;
		const totalCount = data.total || 0;
		const emailItems = data.messages || [];
		
		const emailPreviews = emailItems
			.filter(item => item.subject && item.from && item.from.email && !item.read)
			.map(item => {
				const fromObj = item.from || {};
				return {
					id: item.id,
					subject: item.subject || '(No subject)',
					from: fromObj.email || '(Unknown Sender)',
					fromName: fromObj.name || fromObj.email || '(Unknown Sender)',
					receivedTime: item.received || '(Unknown Time)',
					receivedUnixTime: item.receivedUnixTime || '(Unknown Time)',
					snippet: item.snippet || 'No message preview available.'
				};
			});
		
		this.setState({
			unreadCount,
			emailPreviews,
			totalCount,
			isLoading: false
		});
	};

	getMessagesInfo = async (showLoading = true) => {
		if (showLoading) {
			this.setState({ isLoading: true });
		}
		
		try {
			await this.graphClient.getMessages();
		}
		
		catch (error) {
			this.setState({
				isLoading: false,
				error: 'Failed to retrieve email information: ' + error.message
			});
		}
	};

	getAccountInfo = async () => {
		try {
			await this.graphClient.getProfile();
		}
		catch (error) {
			console.error('Failed to retrieve account information:', error.message);
		}
	};

	handleMouseEnter = () => {
		// Only show flyout on initial hover when it's not already visible
		if (!this.state.flyoutVisible) {
			this.setState({
				isHovering: true,
				flyoutVisible: true
			});
		}
	};

	handleMouseLeave = () => {
		this.setState({ isHovering: false });
		// We don't set flyoutVisible to false here to keep the flyout sticky
	};

	handleDocumentClick = (e) => {
		// Only close flyout when clicking outside and not on "Email" button parent
		if (this.containerRef &&
			!this.containerRef.contains(e.target) &&
			!this.isParentEmailButton(e.target)) {

			// If the flyout was visible and the user is not authenticated, set the cookie
			if (this.state.flyoutVisible && !this.state.isAuthenticated) {
				this.setCookie(COOKIE_NAME, 'true', COOKIE_EXPIRY_DAYS);
			}

			this.setState({
				flyoutVisible: false,
				cookieExists: true
			});
		}
	};

	isParentEmailButton = (target) => {
		let current = target;
		for (let i = 0; i < 5; i++) { // Limit traversal depth
			if (!current || !current.classList) break;
			
			if (current.classList.contains('emailLink') ||
				(current.classList.contains('userLink') &&
				(current.textContent.includes('Email') ||
				current.querySelector('.cube-mail-cube')))) {
				return true;
			}
			current = current.parentElement;
		}
		return false;
	};

	handleClick = (e) => {
		this.setState(prevState => ({
			isHovering: true,
			flyoutVisible: !prevState.flyoutVisible
		}));
		e.stopPropagation();
	};

	handleConnectEmailClick = async (e) => {
		e.preventDefault();
		e.stopPropagation();
		
		try {
			const innerHeight = window.innerHeight;
			const innerWidth = window.innerWidth;
			await this.graphClient.signIn({ innerHeight, innerWidth });
		}
		
		catch (error) {
			this.setState({
				error: 'Failed to start authentication'
			});
		}
	};

	handleMaybeLaterClick = (e) => {
		e.preventDefault();
		e.stopPropagation();

		this.setCookie(COOKIE_NAME, 'true', COOKIE_EXPIRY_DAYS);

		this.setState({
			isHovering: false,
			flyoutVisible: false,
			cookieExists: true
		});
	};

	// Handle hover events from the Email button
	handleEmailButtonHover = (e) => {
		const isHovering = e.detail?.isHovering;
		
		if (isHovering && !this.state.flyoutVisible) {
			this.setState({
				isHovering: true,
				flyoutVisible: true
			});
		}
		
		if (!isHovering) {
			this.setState({ isHovering: false });
		}
	};

	handleSettingsClick = (e) => {
		e.preventDefault();
		e.stopPropagation();
		this.setState({ showSettings: true });
	};

	handleSettingsDone = (e) => {
		e.preventDefault();
		e.stopPropagation();
		this.setState({ showSettings: false });
	};

	handleDisconnect = async (e) => {
		e.preventDefault();
		e.stopPropagation();

		try {
			await this.graphClient.signOut();
		}
		catch (error) {
			console.error('Error signing out:', error);
			// Force a disconnect even if the API call fails
			this.setState({
				isAuthenticated: false,
				unreadCount: 0,
				emailPreviews: [],
				showSettings: false
			});
		}
	};

	// Format date to a readable string
	formatDate = (dateString) => {
		if (!dateString) return '';
		
		try {
			const date = new Date(dateString);
			return date.toLocaleString('en-US', {
				year: 'numeric',
				month: 'short',
				day: 'numeric',
				hour: '2-digit',
				minute: '2-digit'
			});
		}
		
		catch (error) {
			return dateString;
		}
	};

	// Format unix timestamp
	formatUnixTime = (unixTime) => {
		if (!unixTime) return '';
		
		try {
			const date = new Date(unixTime * 1000);
			return date.toLocaleString('en-US', {
				year: 'numeric',
				month: 'short',
				day: 'numeric',
				hour: '2-digit',
				minute: '2-digit'
			});
		}
		
		catch (error) {
			return '';
		}
	};

	handleEmailItemClick = (e, email) => {
		e.preventDefault();
		e.stopPropagation();

		const messageId = email.id;

		const baseUrl = this.props.config.webmailUrl + '/app';

		const separator = baseUrl.includes('?') ? '&' : '?';
		const deepLinkUrl = `${baseUrl}${separator}msgid=${messageId}&app=widget`;

		window.open(deepLinkUrl, '_blank');

		this.setState({
			flyoutVisible: false
		});
	};

	formatTime = (timestamp) => {
		if (!timestamp) return '';
		
		const date = new Date(timestamp);
		const now = new Date();
		const isToday = date.toDateString() === now.toDateString();
		
		if (isToday) {
			return date.toLocaleTimeString('en-US', {
				hour: 'numeric',
				minute: '2-digit',
				hour12: true
			});
		}

		const yesterday = new Date(now);
		yesterday.setDate(yesterday.getDate() - 1);
		if (date.toDateString() === yesterday.toDateString()) {
			return 'Yesterday';
		}

		const diffTime = Math.abs(now - date);
		const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

		if (diffDays <= 7) {
			return date.toLocaleDateString('en-US', { weekday: 'long' });
		}

		return date.toLocaleDateString('en-US', {
			month: 'short',
			day: 'numeric'
		});
	};

	stopPropagation = (e) => e.stopPropagation();

	handleItemClick = (email) => (e) => {
		this.handleEmailItemClick(e, email);
	};

	setContainerRef = (ref) => {
		if (this.containerRef) {
			this.containerRef.removeEventListener('emailButtonHover', this.handleEmailButtonHover);
		}

		this.containerRef = ref;

		if (ref) {
			ref.addEventListener('emailButtonHover', this.handleEmailButtonHover);
		}
	};

	componentDidMount() {
		if (this.props.config?.enabled) {
			this.loadScripts();
			document.addEventListener('click', this.handleDocumentClick);
			if (this.containerRef) {
				this.containerRef.addEventListener('emailButtonHover', this.handleEmailButtonHover);
			}

			this.checkFlyoutCookie();
		}
	}

	componentWillUnmount() {
		document.removeEventListener('click', this.handleDocumentClick);
		if (this.containerRef) {
			this.containerRef.removeEventListener('emailButtonHover', this.handleEmailButtonHover);
		}
		this.clearPollingInterval();
	}

	renderFlyout() {
		const { isAuthenticated, emailPreviews, isLoading, error, sessionExpired, cookieExists, showSettings, userEmail } = this.state;

		if (isAuthenticated) {
			// Show settings view when settings is active
			if (showSettings) {
				return (
					<div class={style.emailPreviewFlyout} onClick={this.stopPropagation}>
						<div>
							<div class={style.flyoutHeader}>
								<div class={style.flyoutTitle}>Email Preview Settings</div>
							</div>
							<div class={style.flyoutContent}>
								<div class={style.settingsContainer}>
									<div class={style.emailSettingRow}>
										<div class={style.userEmailDisplay}>{userEmail}</div>
										<a href="#" onClick={this.handleDisconnect} class={style.disconnectLink}>
											Disconnect
										</a>
									</div>
								</div>
							</div>
							<div class={style.flyoutFooter}>
								<div style="flex: 1;" />
								<a href="#" onClick={this.handleSettingsDone} class={style.doneButton}>
									Done
								</a>
							</div>
						</div>
					</div>
				);
			}

			// Show normal preview when settings is not active
			return (
				<div class={style.emailPreviewFlyout} onClick={this.stopPropagation}>
					<div>
						<div class={style.flyoutHeader}>
							<div class={style.flyoutTitle}>Unread Email Preview</div>
						</div>
						<div class={style.flyoutContent}>
							{isLoading ? (
								<div class={style.loading}>Loading email previews...</div>
							) : error ? (
								<div class={style.error}>{error}</div>
							) : emailPreviews.length > 0 ? (
								<div class={style.previewList}>
									{emailPreviews.map((email, index) => (
										<div
											key={email.id || index}
											class={style.previewItem}
											onClick={this.handleItemClick(email)}
										>
											<div class={style.previewItemHeader}>
												<div class={style.previewFrom}>{email.fromName}</div>
												<div class={style.previewTime}>
													{this.formatTime(email.receivedTime)}
												</div>
											</div>
											<div class={style.previewSubject}>{email.subject}</div>
										</div>
									))}
								</div>
							) : (
								<div class={style.noEmails}>No recent unread emails to preview. Visit your Inbox for more.</div>
							)}
						</div>
						<div class={style.flyoutFooter}>
							<div class={style.footerContent}>
								<a href="#" onClick={this.handleSettingsClick} class={style.settingsButton}>
									<Icon name="cog-wire-cube" class={style.settingsIcon} aria-hidden="true" />
								</a>
								<a href={this.props.config.webmailUrl} target="_blank" rel="noopener noreferrer" class={style.inboxLink}>
									Go to Inbox
								</a>
							</div>
						</div>
					</div>
				</div>
			);
		}

		// Unauthenticated flyout remains unchanged
		return (
			<div class={`${style.emailPreviewFlyout} ${style.unauthenticated}`} onClick={this.stopPropagation}>
				<div class={style.connectFlyoutHeader}>
					<Icon name="alert-bell-wire-cube" aria-hidden="true" />
					<h3>Email Preview & Alerts</h3>
				</div>
				<div class={style.connectFlyoutContent}>
					{sessionExpired ? (
						<p>
							For your security, your login session has expired. Please re-authenticate to continue.
						</p>
					) : (
						<p>
							Connect the CenturyLink portal to your CenturyLink Webmail to get an <em>unread count</em> and <em>unread email preview</em> right in the CenturyLink portal!
						</p>
					)}
				</div>
				<div class={style.connectFlyoutActions}>
					<a href="#" onClick={this.handleConnectEmailClick}>
						Login to Connect Email
					</a>
					<a href="#" onClick={this.handleMaybeLaterClick}>
						{cookieExists ? 'No Thanks' : 'Maybe Later'}
					</a>
				</div>
			</div>
		);
	}

	render() {
		const { isHovering, flyoutVisible, isAuthenticated, unreadCount } = this.state;
		const { config } = this.props;

		if (!config?.enabled) {
			return null;
		}

		const containerClass = cx(
			style.emailPreviewContainer,
			isHovering && style.hovering,
			flyoutVisible && style.flyoutVisible,
			isAuthenticated && 'emailLinkWrapper'
		);

		return (
			<div
				ref={this.setContainerRef}
				class={containerClass}
				onMouseEnter={this.handleMouseEnter}
				onMouseLeave={this.handleMouseLeave}
				onClick={this.handleClick}
			>
				{!isAuthenticated ? (
					<div class={cx(style.notificationBell, style.unauthenticated)}>
						<Icon name="alert-bell-off-wire-cube" aria-hidden="true" />
					</div>
				) : null}
				
				{isAuthenticated && unreadCount > 0 && (
					<div class={style.emailBadge}>
						{unreadCount}
					</div>
				)}
				
				{flyoutVisible && this.renderFlyout()}
			</div>
		);
	}
}